import { useEffect, useState } from 'react';
import type { Vec3 } from './assemblyPose';
import { faceBasis, faceNormal, pointOnFace } from './partFaces';
import { keptHand, lineEnv } from './holeLines';
import { loadSolids, type Solid } from './partSolids';
import type { ByPart } from './partModelSize';
import { isHardwareOrTool, isLeftRight, type PartItem } from './partsRun';
import { holePlace, holeWidthOf, WALL_MM, type StepHole } from './stepHoles';
import type { Job3D } from './threeDApi';
import { namedIn } from './partsCatalogue';
import { useLanguage } from '../../contexts/LanguageContext';

// A HOLE IS NEVER IN THE OPEN. In the assembled product every drilled hole sits inside wood, bar its
// own mouth — where the hardware goes in, onto the part's face. Anywhere else it touches air, the
// line is wrong: it crosses a gap between two parts, its side breaks out of a part, or it runs past
// the last part's far face.
//
// WHY NOT holesOfLine's own check: that one follows the line's CENTRE, and a hole is as wide as its
// hardware — a centre in the wood with the bore half out of the side of the rail passed it. So here
// every run is walked along its centre and along points round its edge, on every component part's
// mesh as the product has it (the other unit of a mirror pair its kept file reflected), and every
// stretch none of them covers is said.
//
// Notes as the joint view says every other: "H101-B · …", under the hole they are about.

// Shorter than this in the open is two meshes cut from one product not quite meeting.
const SLACK_MM = 0.5;
// From this far back along the line, so a crossing count says inside or out.
const FAR = 5000;

const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (a: Vec3, k: number): Vec3 => [a[0] * k, a[1] * k, a[2] * k];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
type Span = [number, number];

// WHERE ONE SOLID IS ALONG A RAY: its crossings in pairs, in and out, as distances from `p`.
const spansOf = (solid: Solid, p: Vec3, d: Vec3): Span[] => {
  const t = solid(add(p, scale(d, -FAR)), d).map((x) => x - FAR);
  const out: Span[] = [];
  for (let i = 0; i + 1 < t.length; i += 2) out.push([t[i], t[i + 1]]);
  return out;
};

// [a, b] less the spans: what of it no wood covers, stretches longer than SLACK_MM.
const uncovered = (a: number, b: number, spans: Span[]): Span[] => {
  const sorted = [...spans].sort((x, y) => x[0] - y[0]);
  const out: Span[] = [];
  let at = a;
  for (const [s, e] of sorted) {
    if (e <= at) continue;
    if (s > at) out.push([at, Math.min(s, b)]);
    at = Math.max(at, e);
    if (at >= b) break;
  }
  if (at < b) out.push([at, b]);
  return out.filter(([s, e]) => e - s > SLACK_MM);
};

const r1 = (n: number) => Math.round(n * 10) / 10;

// EVERY HOLE'S NOTES ABOUT AIR, by hole id: what an AirWalk says. Empty where every hole is in the
// wood — and where a part has no place in the product or no mesh, which other notes already say.
export type AirWalk = (holes: StepHole[], name: (id: string) => string) => Map<string, string[]>;

// THE WALK, SET UP ONCE — every component's mesh loaded — and then asked as often as needed: once per
// joint by the view, once per place by the green area a slot is offered (see holeArea), which is how
// the two say the same thing about a hole.
//
// `solids` and `tried` are a caller's own, shared where it loads the same meshes. `undrilled`: AS THEY
// STOOD BEFORE THEIR FIRST BORE — a drilled one's bores are surfaces too, and a ray down a bore's axis
// would count its walls as wood ending. A run that has undrilled them already passes false.
export async function airWalker(
  items: PartItem[],
  models: ByPart,
  meshOf: (id: string) => Job3D | null,
  solids: Map<string, Solid> = new Map(),
  tried: Set<string> = new Set(),
  undrilled = true,
): Promise<AirWalk> {
  const env = lineEnv(items, models, [], meshOf);
  const components = items.flatMap((p, i) => (!isHardwareOrTool(p) && env.placed.has(String(i)) ? [String(i)] : []));
  await loadSolids(components, meshOf, items, solids, tried, undrilled);
  return (holes, name) => walkAir(holes, items, models, env, solids, name);
}

export async function holeAirNotes(
  holes: StepHole[],
  items: PartItem[],
  models: ByPart,
  meshOf: (id: string) => Job3D | null,
  name: (id: string) => string,
): Promise<Map<string, string[]>> {
  if (!holes.length) return new Map();
  return (await airWalker(items, models, meshOf))(holes, name);
}

function walkAir(
  holes: StepHole[],
  items: PartItem[],
  models: ByPart,
  env: ReturnType<typeof lineEnv>,
  solids: Map<string, Solid>,
  name: (id: string) => string,
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const mx = env.mirrorX;
  const flipP = (p: Vec3): Vec3 => [2 * mx! - p[0], p[1], p[2]];
  const flipD = (d: Vec3): Vec3 => [-d[0], d[1], d[2]];
  // WHERE THE WOOD IS ALONG A RAY, every part's, a pair's other unit included — and whose it is.
  const woodAlong = (p: Vec3, d: Vec3): { id: string; span: Span }[] => [...solids].flatMap(([id, solid]) => [
    ...spansOf(solid, p, d).map((span) => ({ id, span })),
    ...(isLeftRight(items[Number(id)]) && mx != null ? spansOf(solid, flipP(p), flipD(d)).map((span) => ({ id, span })) : []),
  ]);

  // EACH HOLE AS A RAY IN THE PRODUCT: its mouth on the wood and where it ends.
  const rays = holes.flatMap((h) => {
    const box = env.placed.get(h.part);
    if (!box) return [];
    const dims = { x: box.max[0] - box.min[0], y: box.max[1] - box.min[1], z: box.max[2] - box.min[2] };
    const place = holePlace(h, dims);
    const onFace = place && pointOnFace({ face: h.face, u: place.u, v: place.v }, dims);
    if (!onFace) return [];
    let p = add(scale(add(box.min, box.max), 0.5), onFace);
    let d = scale(faceNormal(h.face), -1);
    // THE OTHER UNIT OF A PAIR: its holes are said on the kept file, and stand reflected.
    if (h.side && isLeftRight(items[Number(h.part)]) && mx != null && keptHand(box, mx) !== h.side) {
      p = flipP(p); d = flipD(d);
    }
    const mouth = h.surfaceMm ?? 0;
    let end: number | null = h.depthMm != null && !h.through ? mouth + h.depthMm : null;
    if (h.through) {
      const own = woodAlong(p, d).filter((w) => w.id === h.part && w.span[0] <= mouth + 1 && w.span[1] > mouth + 1);
      end = own.length ? Math.max(...own.map((w) => w.span[1])) : null;
    }
    if (end == null) return [];
    return [{ h, p, d, mouth, end, r: (holeWidthOf(h, items, models) ?? 0) / 2 }];
  });

  // ONE RUN PER LINE AND DIRECTION — a screw's holes in the frame and the rail are one bore through
  // both, the barrel nut's across them another — measured from its first mouth.
  const runs = new Map<string, typeof rays>();
  for (const ray of rays) {
    const key = `${ray.h.line ?? ray.h.id}|${ray.d.map((x) => Math.round(x)).join(',')}`;
    runs.set(key, [...(runs.get(key) ?? []), ray]);
  }
  const say = (id: string, text: string) => out.set(id, [...(out.get(id) ?? []), text]);
  for (const run of runs.values()) {
    const [first] = run;
    const along = (r: (typeof run)[number], t: number) => dot(add(r.p, scale(r.d, t)), first.d) - dot(first.p, first.d);
    // Each hole's stretch along the run, from the first mouth.
    const stretches = run.map((r) => ({ h: r.h, a: along(r, r.mouth), b: along(r, r.end), r: r.r }))
      .sort((x, y) => x.a - y.a);
    const a = stretches[0].a;
    const b = Math.max(...stretches.map((s) => s.b));
    const radius = Math.max(...stretches.map((s) => s.r));
    const holeAt = (t: number) => stretches.find((s) => t >= s.a - SLACK_MM && t <= s.b + SLACK_MM) ?? stretches.find((s) => s.a >= t) ?? stretches[stretches.length - 1];
    // ALONG THE CENTRE: a gap between two parts, or a run past the last.
    const centre = woodAlong(first.p, first.d);
    for (const [s, e] of uncovered(a + SLACK_MM, b, centre.map((w) => w.span))) {
      const before = centre.find((w) => Math.abs(w.span[1] - s) < 1);
      const after = centre.find((w) => Math.abs(w.span[0] - e) < 1);
      const hole = holeAt(e);
      say(hole.h.id, after
        ? `crosses ${r1(e - s)} mm of open air${before ? ` between ${name(before.id)} and ${name(after.id)}` : ` before ${name(after.id)}`}`
        : `runs ${r1(e - s)} mm past ${before ? `${name(before.id)}'s far face` : 'the wood'}`);
    }
    // ROUND ITS EDGE: where the centre is in the wood and the side of the bore is not — or is, with
    // less than WALL_MM of wood round it. A ring of lines WALL_MM out from the bore's side first; only
    // where that finds air is the side itself walked, to say which of the two it is.
    if (radius > 0) {
      const { u, v } = faceBasis(run[0].h.face);
      const centreGaps = uncovered(a + SLACK_MM, b, centre.map((w) => w.span));
      // Each hole's open stretch on a ring `rad` from the axis, by hole and the part it is in: the longest.
      const ring = (rad: number) => {
        const found = new Map<string, { id: string; inside: string | undefined; len: number }>();
        for (let k = 0; k < 8; k++) {
          const ang = (k * Math.PI) / 4;
          const off = add(scale(u, Math.cos(ang) * rad), scale(v, Math.sin(ang) * rad));
          const edge = woodAlong(add(first.p, off), first.d);
          for (const [s, e] of uncovered(a + SLACK_MM, b, edge.map((w) => w.span))) {
            if (centreGaps.some(([cs, ce]) => s < ce && e > cs)) continue;
            const mid = (s + e) / 2;
            const inside = centre.find((w) => w.span[0] <= mid && w.span[1] >= mid)?.id;
            const id = holeAt(mid).h.id;
            const key = `${id}|${inside}`;
            if ((found.get(key)?.len ?? 0) < e - s) found.set(key, { id, inside, len: e - s });
          }
        }
        return found;
      };
      const thin = ring(radius + WALL_MM);
      const broken = thin.size ? ring(radius * 0.9) : new Map();
      for (const [key, f] of new Map([...thin, ...broken])) {
        const out = broken.get(key);
        const what = f.inside ? name(f.inside) : 'the wood';
        say(f.id, out
          ? `its side breaks out of ${what} for ${r1(out.len)} mm`
          : `less than ${WALL_MM} mm of ${what} round it for ${r1(f.len)} mm`);
      }
    }
  }
  // AND BETWEEN TWO BORES SIDE BY SIDE in one part — two barrel nuts across a rail, two screws in a
  // frame: WALL_MM of wood between their sides, or the wood between them splits. Bores along each other
  // (parallel) and over the same stretch; said under both, each naming the other.
  for (let i = 0; i < rays.length; i++) {
    for (let j = i + 1; j < rays.length; j++) {
      const [x, y] = [rays[i], rays[j]];
      if (x.h.part !== y.h.part || !x.r || !y.r || Math.abs(dot(x.d, y.d)) < 0.999) continue;
      const w: Vec3 = [y.p[0] - x.p[0], y.p[1] - x.p[1], y.p[2] - x.p[2]];
      const t = dot(w, x.d);
      const apart = Math.hypot(w[0] - t * x.d[0], w[1] - t * x.d[1], w[2] - t * x.d[2]);
      // Each one's stretch along x's axis, from x's mouth plane.
      const along = (r: typeof x) => [r.mouth, r.end].map((m) => dot(add(r.p, scale(r.d, m)), x.d) - dot(x.p, x.d));
      const [xa, xb] = along(x).sort((m, n) => m - n);
      const [ya, yb] = along(y).sort((m, n) => m - n);
      if (Math.min(xb, yb) - Math.max(xa, ya) <= SLACK_MM) continue;
      const wood = apart - x.r - y.r;
      if (wood >= WALL_MM) continue;
      for (const [a, b] of [[x, y], [y, x]]) {
        say(a.h.id, wood <= 0 ? `runs into ${b.h.id}` : `only ${r1(wood)} mm of wood between it and ${b.h.id}`);
      }
    }
  }
  return out;
}

// THE SAME, KEPT FOR A VIEW: worked out again whenever the holes or the models change, once `ready`
// (the meshes known; never on a run's own copy). Nothing while it is being worked out.
export function useHoleAir(
  holes: StepHole[], items: PartItem[], models: ByPart, meshOf: (id: string) => Job3D | null, ready: boolean,
): Map<string, string[]> {
  const { language } = useLanguage();
  const [air, setAir] = useState<Map<string, string[]>>(new Map());
  useEffect(() => {
    if (!ready) return;
    let gone = false;
    void holeAirNotes(holes, items, models, meshOf, (id) => namedIn(items[Number(id)], language) || `#${id}`)
      .then((got) => { if (!gone) setAir(got); })
      .catch(() => { if (!gone) setAir(new Map()); });
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holes, models, ready, language]);
  return air;
}
